/**
 * SaaS Admin — Cron Registry (SCRUM-312): KPIs, agrupación por dominio,
 * filtros, health-state reconciliado (enabled + últimas corridas reales) y
 * detalle por job con historial. `scheduleActive`/`nextRunAt` reflejan lo
 * declarado en código — Netlify no expone una API general para verificar en
 * vivo qué schedule está deployado, así que no se puede confirmarlo en runtime.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Play, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { cn } from "@/lib/utils";
import { CRON_DOMAIN_LABELS, type CronDomain } from "@/lib/cron-registry";

type CronHealth = "never_run" | "error" | "flaky" | "ok";

type RunRow = {
  id: string;
  jobId: string;
  status: string;
  message: string | null;
  durationMs: number | null;
  executedAt: string;
};

type JobRow = {
  jobId: string;
  label: string;
  description: string;
  enabled: boolean;
  scheduleHint: string | null;
  scheduleActive: boolean;
  endpointPath: string;
  netlifyFn: string | null;
  domain: CronDomain;
  lastStatus: string | null;
  lastRunAt: string | null;
  lastMessage: string | null;
  nextRunAt?: string | null;
  recentErrorCount: number;
  neverRun: boolean;
  health: CronHealth;
  needsAttention: boolean;
  recentRuns: RunRow[];
};

type Kpis = {
  total: number;
  enabled: number;
  disabled: number;
  needsAttention: number;
  neverRun: number;
  scheduleActiveInCode: number;
};

const HEALTH_LABELS: Record<CronHealth, string> = {
  ok: "OK",
  flaky: "Intermitente",
  error: "Error",
  never_run: "Nunca ejecutado",
};

const HEALTH_COLORS: Record<CronHealth, string> = {
  ok: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700",
  flaky: "border-amber-500/40 bg-amber-500/10 text-amber-700",
  error: "border-red-500/40 bg-red-500/10 text-red-700",
  never_run: "border-border bg-muted text-muted-foreground",
};

function KpiTile({ label, value, tone }: { label: string; value: number; tone?: "warn" }) {
  return (
    <div className="rounded-lg border border-border/60 p-3">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={cn("text-xl font-semibold mt-0.5", tone === "warn" && value > 0 && "text-amber-600")}>
        {value}
      </p>
    </div>
  );
}

function fmt(dt: string | null | undefined) {
  return dt ? new Date(dt).toLocaleString("es-AR") : "—";
}

export function AdminCronRegistryPanel() {
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [scheduleNote, setScheduleNote] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<string | null>(null);
  const [scheduleDraft, setScheduleDraft] = useState("");
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [detail, setDetail] = useState<JobRow | null>(null);

  const [search, setSearch] = useState("");
  const [domainFilter, setDomainFilter] = useState<string>("all");
  const [healthFilter, setHealthFilter] = useState<string>("all");
  const [attentionOnly, setAttentionOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiGetJson<{ jobs: JobRow[]; kpis: Kpis; scheduleNote?: string }>(
        "/api/admin/cron-registry",
      );
      setJobs(r.jobs ?? []);
      setKpis(r.kpis ?? null);
      setScheduleNote(r.scheduleNote ?? null);
      setDetail((prev) => (prev ? r.jobs.find((j) => j.jobId === prev.jobId) ?? null : null));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar cron registry");
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (jobId: string, enabled: boolean) => {
    setBusy(jobId);
    try {
      await apiSendJson("/api/admin/cron-registry", "PATCH", { jobId, enabled });
      toast.success(enabled ? "Cron habilitado" : "Cron deshabilitado");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al guardar");
    } finally {
      setBusy(null);
    }
  };

  const saveSchedule = async (jobId: string) => {
    const draft = scheduleDraft.trim();
    setBusy(`sched:${jobId}`);
    setScheduleError(null);
    try {
      await apiSendJson("/api/admin/cron-registry", "PATCH", {
        jobId,
        scheduleHint: draft || null,
      });
      toast.success("Schedule actualizado (registry)");
      setEditingSchedule(null);
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Error al guardar schedule";
      setScheduleError(msg);
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  const runNow = async (jobId: string) => {
    setBusy(`run:${jobId}`);
    try {
      const r = await apiSendJson<{ ok: boolean; status: number; result?: unknown }>(
        "/api/admin/cron-registry",
        "POST",
        { jobId, action: "run" },
      );
      if (r.ok) toast.success(`Ejecutado (${r.status})`);
      else toast.error(`Falló HTTP ${r.status}`);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al ejecutar");
    } finally {
      setBusy(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return jobs.filter((j) => {
      if (attentionOnly && !j.needsAttention) return false;
      if (domainFilter !== "all" && j.domain !== domainFilter) return false;
      if (healthFilter !== "all" && j.health !== healthFilter) return false;
      if (q && !(j.label.toLowerCase().includes(q) || j.jobId.toLowerCase().includes(q) || j.description.toLowerCase().includes(q))) {
        return false;
      }
      return true;
    });
  }, [jobs, search, domainFilter, healthFilter, attentionOnly]);

  const grouped = useMemo(() => {
    const byDomain = new Map<CronDomain, JobRow[]>();
    for (const j of filtered) {
      const arr = byDomain.get(j.domain) ?? [];
      arr.push(j);
      byDomain.set(j.domain, arr);
    }
    return Array.from(byDomain.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando Cron Registry…
      </div>
    );
  }

  const scheduleEditor = (j: JobRow) =>
    editingSchedule === j.jobId ? (
      <div className="space-y-1.5">
        <input
          className="w-full rounded border border-input bg-background px-2 py-1 font-mono text-xs"
          value={scheduleDraft}
          onChange={(e) => setScheduleDraft(e.target.value)}
          placeholder="0 * * * *"
          data-testid={`cron-schedule-input-${j.jobId}`}
        />
        {scheduleError ? <p className="text-red-600 text-[11px]">{scheduleError}</p> : null}
        <div className="flex gap-1">
          <Button
            type="button"
            size="sm"
            variant="default"
            disabled={busy === `sched:${j.jobId}`}
            onClick={() => void saveSchedule(j.jobId)}
          >
            Guardar
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => { setEditingSchedule(null); setScheduleError(null); }}
          >
            Cancelar
          </Button>
        </div>
      </div>
    ) : (
      <>
        <p className="font-mono">{j.scheduleHint ?? "—"}</p>
        <button
          type="button"
          className="text-[11px] text-primary underline"
          onClick={() => {
            setEditingSchedule(j.jobId);
            setScheduleDraft(j.scheduleHint ?? "");
            setScheduleError(null);
          }}
          data-testid={`cron-schedule-edit-${j.jobId}`}
        >
          Editar frecuencia
        </button>
      </>
    );

  return (
    <div className="space-y-4" data-testid="admin-cron-registry">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Cron Registry</h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Toggle <code>enabled</code> sin redeploy (gate en jobs que usan{" "}
            <code>runCronJob</code>). Health calculado sobre corridas reales de{" "}
            <code>sm_cron_runs</code>. {scheduleNote}
          </p>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={() => void load()}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Refrescar
        </Button>
      </div>

      {kpis ? (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <KpiTile label="Total" value={kpis.total} />
          <KpiTile label="Habilitados" value={kpis.enabled} />
          <KpiTile label="Deshabilitados" value={kpis.disabled} />
          <KpiTile label="Requieren atención" value={kpis.needsAttention} tone="warn" />
          <KpiTile label="Nunca ejecutados" value={kpis.neverRun} />
          <KpiTile label="Schedule ON (código)" value={kpis.scheduleActiveInCode} />
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2 items-center">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar job…"
          className="h-9 w-[200px] text-xs"
        />
        <Select value={domainFilter} onValueChange={setDomainFilter}>
          <SelectTrigger className="h-9 w-[160px] text-xs"><SelectValue placeholder="Dominio" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los dominios</SelectItem>
            {Object.entries(CRON_DOMAIN_LABELS).map(([k, label]) => (
              <SelectItem key={k} value={k}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={healthFilter} onValueChange={setHealthFilter}>
          <SelectTrigger className="h-9 w-[170px] text-xs"><SelectValue placeholder="Health" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(HEALTH_LABELS).map(([k, label]) => (
              <SelectItem key={k} value={k}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={() => setAttentionOnly((v) => !v)}
          className={cn(
            "h-9 rounded-md border px-3 text-xs font-medium inline-flex items-center gap-1.5",
            attentionOnly
              ? "border-amber-500/50 bg-amber-500/10 text-amber-700"
              : "border-border text-muted-foreground",
          )}
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          Requiere atención
        </button>
      </div>

      {grouped.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">Sin jobs para estos filtros.</p>
      ) : null}

      {grouped.map(([domain, domainJobs]) => (
        <div key={domain} className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {CRON_DOMAIN_LABELS[domain]} <span className="font-normal">({domainJobs.length})</span>
          </h3>

          {/* Mobile: cards */}
          <div className="md:hidden space-y-2">
            {domainJobs.map((j) => (
              <div key={j.jobId} className="rounded-lg border border-border/60 p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-sm">{j.label}</p>
                    <p className="font-mono text-[11px] text-muted-foreground">{j.jobId}</p>
                  </div>
                  <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium border shrink-0", HEALTH_COLORS[j.health])}>
                    {HEALTH_LABELS[j.health]}
                  </span>
                </div>
                <div className="text-xs space-y-1">{scheduleEditor(j)}</div>
                <p className="text-[11px] text-muted-foreground">
                  Últ.: {fmt(j.lastRunAt)}{j.lastStatus ? ` · ${j.lastStatus}` : ""}
                </p>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    disabled={busy === j.jobId}
                    onClick={() => void toggle(j.jobId, !j.enabled)}
                    className={cn(
                      "rounded-full px-2.5 py-1 text-xs font-medium border",
                      j.enabled
                        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700"
                        : "border-border bg-muted text-muted-foreground",
                    )}
                    data-testid={`cron-toggle-${j.jobId}`}
                  >
                    {j.enabled ? "ON" : "OFF"}
                  </button>
                  <Button type="button" size="sm" variant="secondary" disabled={busy === `run:${j.jobId}`} onClick={() => void runNow(j.jobId)} data-testid={`cron-run-${j.jobId}`}>
                    {busy === `run:${j.jobId}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />}
                    Run
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => setDetail(j)}>
                    Detalle
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: table */}
          <div className="hidden md:block overflow-x-auto rounded-lg border border-border/60">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left p-3 font-medium">Job</th>
                  <th className="text-left p-3 font-medium">Schedule</th>
                  <th className="text-left p-3 font-medium">Health</th>
                  <th className="text-left p-3 font-medium">Enabled</th>
                  <th className="text-left p-3 font-medium">Última corrida</th>
                  <th className="text-right p-3 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {domainJobs.map((j) => (
                  <tr key={j.jobId} className="border-t border-border/50 align-top">
                    <td className="p-3">
                      <p className="font-medium">{j.label}</p>
                      <p className="font-mono text-[11px] text-muted-foreground">{j.jobId}</p>
                      <p className="text-xs text-muted-foreground mt-1">{j.description}</p>
                    </td>
                    <td className="p-3 text-xs space-y-1.5">
                      {scheduleEditor(j)}
                      <p className={cn("mt-1", j.scheduleActive ? "text-emerald-600" : "text-amber-600")}>
                        {j.scheduleActive ? "schedule ON (código)" : "schedule OFF (código)"}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        Próx.: {j.nextRunAt ? new Date(j.nextRunAt).toLocaleString("es-AR") : "—"}
                      </p>
                    </td>
                    <td className="p-3">
                      <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium border", HEALTH_COLORS[j.health])}>
                        {HEALTH_LABELS[j.health]}
                      </span>
                      {j.recentErrorCount > 0 ? (
                        <p className="text-[11px] text-red-600 mt-1">{j.recentErrorCount} error(es) recientes</p>
                      ) : null}
                    </td>
                    <td className="p-3">
                      <button
                        type="button"
                        disabled={busy === j.jobId}
                        onClick={() => void toggle(j.jobId, !j.enabled)}
                        className={cn(
                          "rounded-full px-2.5 py-1 text-xs font-medium border",
                          j.enabled
                            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700"
                            : "border-border bg-muted text-muted-foreground",
                        )}
                        data-testid={`cron-toggle-${j.jobId}`}
                      >
                        {j.enabled ? "ON" : "OFF"}
                      </button>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">
                      <p>{j.lastStatus ?? "—"}</p>
                      <p>{fmt(j.lastRunAt)}</p>
                      {j.lastMessage ? <p className="truncate max-w-[220px]">{j.lastMessage}</p> : null}
                    </td>
                    <td className="p-3 text-right space-x-1.5 whitespace-nowrap">
                      <Button type="button" size="sm" variant="outline" onClick={() => setDetail(j)}>
                        Detalle
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={busy === `run:${j.jobId}`}
                        onClick={() => void runNow(j.jobId)}
                        data-testid={`cron-run-${j.jobId}`}
                      >
                        {busy === `run:${j.jobId}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />}
                        Run
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <Dialog open={!!detail} onOpenChange={(open) => { if (!open) setDetail(null); }}>
        <DialogContent className="max-w-lg">
          {detail ? (
            <>
              <DialogHeader>
                <DialogTitle>{detail.label}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium border", HEALTH_COLORS[detail.health])}>
                    {HEALTH_LABELS[detail.health]}
                  </span>
                  <span className="text-xs text-muted-foreground">{CRON_DOMAIN_LABELS[detail.domain]}</span>
                </div>
                <p className="text-xs text-muted-foreground">{detail.description}</p>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <dt className="text-muted-foreground">Job ID</dt><dd className="font-mono">{detail.jobId}</dd>
                  <dt className="text-muted-foreground">Endpoint</dt><dd className="font-mono">{detail.endpointPath}</dd>
                  <dt className="text-muted-foreground">Netlify fn</dt><dd className="font-mono">{detail.netlifyFn ?? "—"}</dd>
                  <dt className="text-muted-foreground">Schedule (código)</dt><dd className="font-mono">{detail.scheduleHint ?? "—"}</dd>
                  <dt className="text-muted-foreground">Próx. corrida est.</dt><dd>{fmt(detail.nextRunAt)}</dd>
                </dl>
                <div>
                  <p className="text-xs font-semibold mb-1.5">Últimas corridas</p>
                  {detail.recentRuns.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Sin corridas registradas.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {detail.recentRuns.map((run) => (
                        <li key={run.id} className="text-xs border-t border-border/50 pt-1.5 first:border-0 first:pt-0">
                          <span className={run.status === "error" ? "text-red-600" : "text-emerald-600"}>{run.status}</span>
                          {" · "}{fmt(run.executedAt)}
                          {run.durationMs != null ? ` · ${run.durationMs}ms` : ""}
                          {run.message ? <p className="text-muted-foreground truncate">{run.message}</p> : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
